import { useState, useRef } from "react";
import { useErrorBoundary } from "react-error-boundary";
import { callApi, handleErrorCommon } from "../common/api/Api";
import { SERIAL_NUMBER_OF_DIGITS } from "../common/settings/Constants";
import "./Serial.css";
import { Serial, addSerial } from "./SerialApi";
import TextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import { CommonDialog } from "./CommonDialog";
import { ErrorDialog } from "../common/components/ErrorDialog";
import Button from "@mui/material/Button";

interface Props {
  onRegister: (serial: Serial) => void;
  serialList: Serial[];
}

/**
 * カメラ追加ボタンのコンポーネントです。
 *
 * @returns カメラ追加ボタンのコンポーネント
 */
export const RegisterSerialButton = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const { showBoundary } = useErrorBoundary();
  const [inputDialogOpen, setInputDialogOpen] = useState(false);
  const inputSerialNumRef = useRef<HTMLInputElement>();
  const [inputSerialNum, setInputSerialNum] = useState<string>();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const inputContent = (
    <div className="text-base">
      <TextField
        id="inputSerialNumber"
        defaultValue={inputSerialNum}
        variant="outlined"
        margin="normal"
        label={t("common.serialNumber")}
        type="text"
        size="medium"
        autoFocus
        inputProps={{ maxLength: SERIAL_NUMBER_OF_DIGITS }}
        inputRef={inputSerialNumRef}
      />
    </div>
  );

  const onCloseErrorDialog = () => {
    setErrorDialogOpen(false);
  };

  const isNumber = (target: string) => {
    const pattern = /^\d*$/;
    return pattern.test(target);
  };

  const onConfirm = () => {
    const e = inputSerialNumRef.current;
    if (e && e.value) {
      const inputSerial = e.value.toUpperCase();
      setInputSerialNum(inputSerial);
      const inputSerialNum = inputSerial.substring(2); // シリアルナンバーから数字のみの部分のみを抽出

      if (
        inputSerial.length !== SERIAL_NUMBER_OF_DIGITS ||
        !inputSerial.startsWith("YW") ||
        !isNumber(inputSerialNum)
      ) {
        setErrorMessage(t("serial.invalidSerialErrorMessage"));
        setErrorDialogOpen(true);
      } else if (props.serialList && props.serialList.some((value) => value.serialNumber === inputSerial)) {
        setErrorMessage(t("serial.existingSerialErrorMessage"));
        setErrorDialogOpen(true);
      } else {
        setConfirmDialogOpen(true);
      }
    }
  };

  const confirmContent = (
    <div>
      <div className="text-base">{t("serial.confirmSerialNumberMessage")}</div>
      <div className="text-base">{t("serial.confirmSerialNumberInfoMessage")}</div>
      <div className="text-3xl">{inputSerialNum}</div>
    </div>
  );

  const onRegister = () => {
    if (!inputSerialNum) {
      return;
    }

    setInputDialogOpen(false);
    setConfirmDialogOpen(false);
    setInputSerialNum("");

    (async () => {
      await callApi(async () => {
        const result = await addSerial(inputSerialNum);
        if (result.error) {
          return handleErrorCommon(result.error?.response?.data, result.error, showBoundary, t);
        }

        if (!result.data) {
          return;
        }
        props.onRegister(result.data);
      });
    })();
  };

  return (
    <>
      <Button
        variant="contained"
        onClick={() => {
          setInputDialogOpen(true);
        }}
      >
        <div>
          <ul>{t("serial.addCameraButton")}</ul>
          <ul>{t("serial.addCameraButtonSideNote")}</ul>
        </div>
      </Button>

      {/* 入力ダイアログ */}
      <CommonDialog
        open={inputDialogOpen}
        title={t("serial.inputSerialNumberMessage")}
        acceptButtonName={t("common.informButton")}
        onAccept={onConfirm}
        closeButtonName={t("common.cancelButton")}
        onClose={() => {
          setInputDialogOpen(false);
          setInputSerialNum("");
        }}
      >
        {inputContent}
      </CommonDialog>

      {/* 確認ダイアログ */}
      <CommonDialog
        open={confirmDialogOpen}
        title={t("serial.confirmSerialNumberTitle")}
        acceptButtonName={t("common.registerButton")}
        onAccept={onRegister}
        closeButtonName={t("common.backButton")}
        onClose={() => {
          setConfirmDialogOpen(false);
        }}
      >
        {confirmContent}
      </CommonDialog>

      {/* エラーダイアログ */}
      <ErrorDialog open={errorDialogOpen} message={errorMessage} onAccept={onCloseErrorDialog} />
    </>
  );
};
